import React from 'react';
import { DIVISOR_CUTOFF_QUESTION, DIVISOR_CUTOFF_RULE } from '../../data/compPlan';
import { AdjustedCalc } from '../../types/commission';
import { formatPct, formatUsd } from '../../utils/format';
import { Badge } from '../sf/Badge';
import { NewBadge } from '../sf/NewBadge';
import { OpenQuestionBadge } from '../sf/OpenQuestionBadge';
export function AdjustedAmountBlock({
  calc
}: {
  calc: AdjustedCalc;
}) {
  const rows = [{
    label: 'Loan amount',
    value: formatUsd(calc.loanAmount)
  }, {
    label: '× Total Comp Corr %',
    value: formatPct(calc.totalCompPct)
  }, {
    label: '= Comp revenue',
    value: formatUsd(calc.revenue)
  }, {
    label: '− Fee short (halved)',
    value: formatUsd(calc.feeShort)
  }, {
    label: '= Revenue after fee short',
    value: formatUsd(calc.revenueAfterShort),
    negative: calc.revenueAfterShort < 0
  }, {
    label: 'Adjusted Total Comp Corr %',
    value: `${calc.adjustedCompPct.toFixed(4)}%`
  }, ...(calc.negative ? [] : [{
    label: `÷ Divisor (${calc.divisorRuleKey})`,
    value: formatPct(calc.divisorPct)
  }])];
  return <section aria-label="Adjusted Loan Amount" data-id="element-468" data-mp="2fz3q5yprgwe7">
      <h3 className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold text-sf-weak" data-id="element-469" data-mp="2kzi05maxagev">
        Adjusted Loan Amount
        {calc.negative && <Badge tone="warning" data-id="element-470" data-mp="2pxb386gxiuax">Negative comp</Badge>}
      </h3>
      <p className="mb-1.5 text-xs text-sf-weak" data-id="element-471" data-mp="2ur5ubuyv6bk1">
        Comp type: <span className="text-sf-text" data-id="element-472" data-mp="2yrpruzncnh6p">{calc.compType}</span> · Product: <span className="text-sf-text" data-id="element-473" data-mp="2yrg1haye780h">{calc.product}</span>
        {calc.rateLockDate && <>
            {' '}
            · Rate Lock Date: <span className="text-sf-text" data-id="element-474" data-mp="38q5b6gg4xm5n">{calc.rateLockDate}</span>
            {calc.divisorReason && ` (${calc.divisorReason})`}
          </>}
      </p>
      <div className="rounded border border-sf-border" data-id="element-475" data-mp="3dmvz1py6lm1m">
        <dl className="px-3 py-2" data-id="element-476" data-mp="1ecmc326ghvi8">
          {rows.map(row => <div key={row.label} className="flex justify-between gap-3 py-0.5 text-[13px]" data-id="element-477" data-mp="1jaxlxcwr460g">
              <dt className="text-sf-weak" data-id="element-478" data-mp="1ba9l3gry7c7i">{row.label}</dt>
              <dd className={`tabular-nums ${'negative' in row && row.negative ? 'text-sf-red' : ''}`} data-id="element-479" data-mp="1baroqygxwspg">{row.value}</dd>
            </div>)}
        </dl>
        <div className="flex justify-between gap-3 border-t border-sf-border bg-sf-bg px-3 py-2 text-[13px] font-semibold" data-id="element-480" data-mp="1l8ytmixyfkb3">
          <span data-id="element-481" data-mp="1g8jgnshl4etn">= Adjusted Loan Amount</span>
          <span className="tabular-nums" data-id="element-482" data-mp="1gbp5r7q7rgpd">{formatUsd(calc.adjustedAmount)}</span>
        </div>
      </div>
      <p className="mt-1.5 text-xs text-sf-weak" data-id="element-483" data-mp="1pa0u133ly2en">
        Fee short = ({formatUsd(calc.threshold)} target − {formatUsd(calc.processingCollected)} processing collected − {formatUsd(calc.underwritingCollected)} UW/admin) ÷ 2 ={' '}
        {formatUsd(calc.feeShort)}. Correspondent non-HELOC only. If comp % is at or above the divisor, the Adjusted Loan Amount is the raw loan amount.
      </p>
      <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-sf-weak" data-id="element-484" data-mp="2cx8xdfhh71e1">
        {DIVISOR_CUTOFF_RULE}. <OpenQuestionBadge data-id="element-485" data-mp="2rmam8fjter7i" />
        <span data-id="element-486" data-mp="2rm2boub5zxzh">{DIVISOR_CUTOFF_QUESTION}</span>
      </p>
      {calc.negative && <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-sf-weak" data-id="element-487" data-mp="2wl57do598rus">
          Revenue after fee short is negative → comp % &lt; 0 → Adjusted Loan Amount $0.00 (main).
          <NewBadge label="NEW / requested: flat $500 LO / $250 TL — not applied" data-id="element-488" data-mp="2nkp1y94ybana" />
        </p>}
    </section>;
}